import { useCallback, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { callPymbleApi } from "./usePymbleApi";

/**
 * The photo consent files for one event, built by pymble-apis.
 *
 * | Verb | Path                                    | Returns              | Auth         |
 * |------|-----------------------------------------|----------------------|--------------|
 * | GET  | /consent/report/event/{event_id}/export | Excel workbook       | active staff |
 * | GET  | /consent/report/event/{event_id}/print  | Photo consent sheet  | active staff |
 *
 * The server builds both files, so the rows never pass through this app as
 * data: the response is a Blob that goes straight to a download link. The list
 * is everyone going (people linked directly plus members of the event's
 * selected groups), so it can be longer than the Attendees tab.
 *
 * ⚠️ PII. The files hold names and consent decisions. Rules that apply here:
 *   - nothing is cached: no React Query entry, no storage, no service worker
 *   - the object URL is revoked as soon as the download starts
 *   - failures are reported by the caller with a fixed message and no detail,
 *     because an error body could quote the request back
 */

export type ConsentReportFile = "export" | "print";

const CONSENT_REPORT_END_POINT = "consent/report/event/";

/**
 * Today's date in Sydney as `YYYY-MM-DD`.
 *
 * The API names its files from the same Sydney date, but the name it sends is
 * in `Content-Disposition`, which the axios wrapper does not hand back. Naming
 * the download here keeps it to the same pattern; a blob link would otherwise be
 * named by UUID.
 */
function sydneyDate(): string {
	// en-CA formats a date as YYYY-MM-DD.
	return new Intl.DateTimeFormat("en-CA", {
		timeZone: "Australia/Sydney",
		year: "numeric",
		month: "2-digit",
		day: "2-digit",
	}).format(new Date());
}

/** Kind and date only: an event title can name a person, so it stays out. */
export function consentReportFilename(file: ConsentReportFile): string {
	return file === "export"
		? `consent-event-${sydneyDate()}.xlsx`
		: `consent-sheet-event-${sydneyDate()}.pdf`;
}

export function useConsentReportFile(eventId?: string) {
	const { instance } = useMsal();
	const [preparing, setPreparing] = useState<ConsentReportFile | null>(null);

	/**
	 * Fetch one file and save it.
	 *
	 * Rejects on any failure, including a null answer (MSAL had no account or
	 * went to a redirect), so the caller has one place to show its toast.
	 */
	const download = useCallback(
		async (file: ConsentReportFile) => {
			if (!eventId) throw new Error("No event");

			setPreparing(file);
			try {
				const data: unknown = await callPymbleApi(
					instance,
					"GET",
					`${CONSENT_REPORT_END_POINT}${encodeURIComponent(eventId)}/${file}`,
					{ responseType: "blob" }
				);
				if (!(data instanceof Blob) || !data.size) {
					throw new Error("No file");
				}

				const url = URL.createObjectURL(data);
				const link = document.createElement("a");
				link.href = url;
				link.download = consentReportFilename(file);
				link.click();
				URL.revokeObjectURL(url);
			} finally {
				setPreparing(null);
			}
		},
		[instance, eventId]
	);

	return { download, preparing };
}
